import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Box, Typography, Button } from "@mui/material";
import { Lock } from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import { Spinner } from "../common/LazyLoadWrapper";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
  fallback?: React.ReactNode;
}

const AccessDenied: React.FC = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
      backgroundColor: "var(--bg)",
      p: 3,
    }}
  >
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        maxWidth: 400,
        width: "100%",
        backgroundColor: "var(--bg-elev)",
        borderRadius: 2,
        p: 4,
        boxShadow: 3,
      }}
    >
      <Lock color="error" sx={{ fontSize: 48, mb: 2 }} />
      <Typography variant="h5" gutterBottom align="center">
        Access Denied
      </Typography>
      <Typography
        variant="body1"
        align="center"
        sx={{ color: "var(--ink-3)", mb: 3 }}
      >
        You don't have permission to access this resource. Please contact your
        administrator if you believe this is an error.
      </Typography>
      <Button variant="contained" onClick={() => window.history.back()}>
        Go Back
      </Button>
    </Box>
  </Box>
);

const LoadingScreen: React.FC = () => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
      gap: 16,
      background: "var(--bg, #f5f5f5)",
    }}
  >
    <Spinner size={52} />
    <span style={{ color: "var(--ink-3, #888)", fontSize: 14 }}>
      Loading...
    </span>
  </div>
);

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles,
  fallback = <AccessDenied />,
}) => {
  const { isAuthenticated, loading, user, hasRole } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated || !user) {
    // Redirect to login with the current location so we can redirect back after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user has required roles
  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some((role) => hasRole(role));
    if (!hasRequiredRole) {
      return <>{fallback}</>;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;

// Specific role-based route components
export const AdminRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <ProtectedRoute requiredRoles={["Admin", "SuperAdmin"]}>
    {children}
  </ProtectedRoute>
);

export const ManagerRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <ProtectedRoute requiredRoles={["Manager", "Admin", "SuperAdmin"]}>
    {children}
  </ProtectedRoute>
);

export const UserRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <ProtectedRoute requiredRoles={["User", "Manager", "Admin", "SuperAdmin"]}>
    {children}
  </ProtectedRoute>
);

export const AccountManagerRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAccountManager } = useAuth();
  return (
    <ProtectedRoute>
      {isAccountManager ? <>{children}</> : <AccessDenied />}
    </ProtectedRoute>
  );
};
